import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Textarea } from "../components/ui/textarea";
import { Switch } from "../components/ui/switch";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import {
  ArrowLeft,
  Heart,
  Clock,
  CheckCircle,
  AlertTriangle,
  Phone,
  MessageSquare,
  Settings
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const VeteranFollowUp = ({ user }) => {
  const navigate = useNavigate();
  const [followUps, setFollowUps] = useState([]);
  const [preferences, setPreferences] = useState({
    receive_follow_ups: true,
    follow_up_24h: true,
    follow_up_7d: true
  });
  const [isLoading, setIsLoading] = useState(true);
  const [isSavingPrefs, setIsSavingPrefs] = useState(false);
  const [respondingTo, setRespondingTo] = useState(null);
  const [responseMessage, setResponseMessage] = useState("");
  const [isResponding, setIsResponding] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [followUpsRes, prefsRes] = await Promise.all([
        axios.get(`${API}/veteran/follow-ups`),
        axios.get(`${API}/veteran/follow-up-preferences`)
      ]);
      
      setFollowUps(followUpsRes.data.follow_ups || []);
      setPreferences(prefsRes.data);
    } catch (error) {
      console.error("Failed to load follow-up data:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSavePreferences = async () => {
    setIsSavingPrefs(true);
    try {
      await axios.put(`${API}/veteran/follow-up-preferences`, null, {
        params: preferences
      });
      toast.success("Preferences saved");
    } catch (error) {
      toast.error("Failed to save preferences");
    } finally {
      setIsSavingPrefs(false);
    }
  };

  const handleRespond = async (followUpId, response) => {
    setIsResponding(true);
    try {
      await axios.post(`${API}/veteran/follow-ups/${followUpId}/respond`, {
        response: response,
        message: responseMessage
      });
      
      toast.success("Thank you for checking in");
      setRespondingTo(null);
      setResponseMessage("");
      loadData();
      
      if (response === "crisis") {
        // Show crisis resources
        navigate("/crisis-resources");
      }
    } catch (error) {
      toast.error("Failed to submit response");
    } finally {
      setIsResponding(false);
    }
  };

  const formatDueDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffHours = Math.round((date - now) / (1000 * 60 * 60));
    
    if (diffHours < 0) {
      return "Due now";
    } else if (diffHours < 24) {
      return `Due in ${diffHours} hour${diffHours !== 1 ? 's' : ''}`;
    } else {
      const diffDays = Math.round(diffHours / 24);
      return `Due in ${diffDays} day${diffDays !== 1 ? 's' : ''}`;
    }
  };

  const getFollowUpLabel = (type) => {
    switch (type) {
      case "24h": return "24-Hour Check-in";
      case "7d": return "7-Day Follow-up";
      default: return "Check-in";
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="bg-card border-b border-border sticky top-0 z-10">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <Button variant="ghost" size="icon" onClick={() => navigate("/dashboard")}>
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <div>
                <h1 className="text-xl font-bold" style={{ fontFamily: 'Manrope' }}>
                  Follow-Up Check-ins
                </h1>
                <p className="text-sm text-muted-foreground">
                  We care about how you're doing
                </p>
              </div>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Pending Follow-ups */}
        {followUps.length > 0 && (
          <div className="mb-8">
            <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <Heart className="w-5 h-5 text-red-500" />
              Pending Check-ins
            </h2>
            
            <div className="space-y-4">
              {followUps.map((followUp) => (
                <Card key={followUp.follow_up_id} className="card-valor" data-testid={`followup-${followUp.follow_up_id}`}>
                  <CardHeader className="pb-2">
                    <div className="flex items-center justify-between">
                      <CardTitle className="text-base">{getFollowUpLabel(followUp.type)}</CardTitle>
                      <Badge variant="outline">
                        <Clock className="w-3 h-3 mr-1" />
                        {formatDueDate(followUp.due_at)}
                      </Badge>
                    </div>
                  </CardHeader>
                  <CardContent>
                    {respondingTo === followUp.follow_up_id ? (
                      <div className="space-y-4">
                        <p className="text-sm text-muted-foreground">
                          How are you feeling since your last support session?
                        </p>
                        
                        <Textarea
                          value={responseMessage}
                          onChange={(e) => setResponseMessage(e.target.value)}
                          placeholder="Share any thoughts (optional)..."
                          rows={3}
                          data-testid="followup-message-input"
                        />
                        
                        <div className="grid grid-cols-3 gap-2">
                          <Button
                            variant="outline"
                            className="flex flex-col items-center gap-1 h-auto py-3 border-green-200 hover:bg-green-50"
                            onClick={() => handleRespond(followUp.follow_up_id, "doing_well")}
                            disabled={isResponding}
                            data-testid="response-doing-well"
                          >
                            <CheckCircle className="w-5 h-5 text-green-600" />
                            <span className="text-xs">Doing Well</span>
                          </Button>
                          
                          <Button
                            variant="outline"
                            className="flex flex-col items-center gap-1 h-auto py-3 border-yellow-200 hover:bg-yellow-50"
                            onClick={() => handleRespond(followUp.follow_up_id, "need_support")}
                            disabled={isResponding}
                            data-testid="response-need-support"
                          >
                            <MessageSquare className="w-5 h-5 text-yellow-600" />
                            <span className="text-xs">Need Support</span>
                          </Button>
                          
                          <Button
                            variant="outline"
                            className="flex flex-col items-center gap-1 h-auto py-3 border-red-200 hover:bg-red-50"
                            onClick={() => handleRespond(followUp.follow_up_id, "crisis")}
                            disabled={isResponding}
                            data-testid="response-crisis"
                          >
                            <AlertTriangle className="w-5 h-5 text-red-600" />
                            <span className="text-xs">In Crisis</span>
                          </Button>
                        </div>
                        
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => setRespondingTo(null)}
                          className="w-full"
                        >
                          Cancel
                        </Button>
                      </div>
                    ) : (
                      <div className="flex items-center justify-between">
                        <p className="text-sm text-muted-foreground">
                          Let us know how you're doing
                        </p>
                        <Button 
                          size="sm"
                          onClick={() => setRespondingTo(followUp.follow_up_id)}
                          data-testid={`respond-btn-${followUp.follow_up_id}`}
                        >
                          Check In
                        </Button>
                      </div>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        )}

        {followUps.length === 0 && (
          <Card className="card-valor mb-8">
            <CardContent className="p-8 text-center">
              <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-4" />
              <h3 className="text-lg font-semibold mb-2">All caught up!</h3>
              <p className="text-muted-foreground">
                No pending follow-ups right now. We'll check in after your next support session.
              </p>
            </CardContent>
          </Card>
        )}

        {/* Crisis Resources Quick Access */}
        <Card className="card-valor mb-8 bg-red-50 border-red-200">
          <CardContent className="p-4">
            <div className="flex items-start gap-4">
              <Phone className="w-6 h-6 text-red-600 flex-shrink-0" />
              <div>
                <h3 className="font-semibold text-red-800">Need immediate help?</h3>
                <p className="text-sm text-red-700 mt-1">
                  If you're in crisis, please reach out:
                </p>
                <div className="mt-2 space-y-1">
                  <a href="tel:988" className="block text-sm font-semibold text-red-800 hover:underline">
                    988 - Suicide & Crisis Lifeline (Press 1 for Veterans)
                  </a>
                  <a href="tel:911" className="block text-sm text-red-700 hover:underline">
                    911 - Emergency Services
                  </a>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Preferences */}
        <Card className="card-valor">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Settings className="w-5 h-5 text-primary" />
              Follow-Up Preferences
            </CardTitle>
            <CardDescription>
              Control when we check in with you
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <Label>Receive follow-up check-ins</Label>
                <p className="text-sm text-muted-foreground">
                  We'll reach out after your support sessions
                </p>
              </div>
              <Switch
                checked={preferences.receive_follow_ups}
                onCheckedChange={(checked) => setPreferences({...preferences, receive_follow_ups: checked})}
                data-testid="pref-receive-followups"
              />
            </div>
            
            {preferences.receive_follow_ups && (
              <>
                <div className="flex items-center justify-between pl-4 border-l-2 border-muted">
                  <div>
                    <Label>24-hour check-in</Label>
                    <p className="text-sm text-muted-foreground">
                      Quick check-in the day after your session
                    </p>
                  </div>
                  <Switch
                    checked={preferences.follow_up_24h}
                    onCheckedChange={(checked) => setPreferences({...preferences, follow_up_24h: checked})}
                    data-testid="pref-24h"
                  />
                </div>
                
                <div className="flex items-center justify-between pl-4 border-l-2 border-muted">
                  <div>
                    <Label>7-day follow-up</Label>
                    <p className="text-sm text-muted-foreground">
                      Longer-term check-in a week later
                    </p>
                  </div>
                  <Switch
                    checked={preferences.follow_up_7d}
                    onCheckedChange={(checked) => setPreferences({...preferences, follow_up_7d: checked})}
                    data-testid="pref-7d"
                  />
                </div>
              </>
            )}
            
            <Button 
              onClick={handleSavePreferences} 
              disabled={isSavingPrefs}
              className="w-full"
              data-testid="save-followup-prefs-btn"
            >
              {isSavingPrefs ? "Saving..." : "Save Preferences"}
            </Button>
          </CardContent>
        </Card>
      </main>
    </div>
  );
};

export default VeteranFollowUp;
